/**
 * Mobile-WebView detection, extracted from the pattern proven in
 * `views/Subscription/Subscription.js`.
 *
 * Pages opened inside the native CustomWebView arrive with a `?token=` deep-link
 * param; desktop web has none. That param does not survive the Stripe round-trip
 * (SEC-4 strips it before redirecting, and Stripe returns to a bare URL), so the
 * fact that we are in a WebView is persisted in sessionStorage — scoped to this
 * tab/WebView session, and holding only a boolean marker, never the token.
 */
const WEB_VIEW_KEY = "isWebView";

/** Persist the marker. Call on any page that received a `?token=` deep link. */
export const markWebViewSession = () => {
  try {
    sessionStorage.setItem(WEB_VIEW_KEY, "true");
  } catch {
    // Private-mode / storage-disabled: detection degrades to the token check.
  }
};

/**
 * True when this page is running inside the mobile WebView — either the token
 * param is present now, or the marker was set earlier in this session.
 */
export const isWebViewSession = (token) => {
  if (token) {
    return true;
  }
  try {
    return sessionStorage.getItem(WEB_VIEW_KEY) === "true";
  } catch {
    return false;
  }
};

const HANDOFF_KEY = "taxHandoff";

/**
 * The page was opened from the app with a one-time code that is gone from the
 * URL by the time anything reloads. The marker lets a reload say "open it from
 * the app again" instead of showing a sign-in prompt a mobile-only user cannot
 * satisfy. Storage can be blocked (Safari "block all cookies", some in-app
 * browsers), in which case a reload degrades to that sign-in prompt.
 */
export const markHandoffSession = () => {
  try {
    sessionStorage.setItem(HANDOFF_KEY, "true");
  } catch {
    // Storage blocked: reload falls back to the sign-in prompt.
  }
};

export const isHandoffSession = () => {
  try {
    return sessionStorage.getItem(HANDOFF_KEY) === "true";
  } catch {
    return false;
  }
};
